/* eslint-disable @next/next/no-img-element */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import {
  ChangeEventHandler,
  FC,
  FormEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { FaTrash, FaUpload } from 'react-icons/fa';

import { useTheme } from 'styled-components';
import { ICategoryResponse } from '~/modules/categories/interfaces/i-category-response';

import { useToast } from '~/common/providers/toast';
import { useAuth } from '~/common/providers/auth';
import { useMediaQuery } from '~/common/hooks/use-media-query';
import { UserTypesEnum } from '~/common/enums/user-types.enum';
import {
  UserSubscriptionTypesEnum,
  UserSubscriptionTypesEnumFormatted,
} from '~/common/enums/user-subscription-types.enum';
import { Typography } from '~/common/components/Typograph';
import { TextInput } from '~/common/components/TextInput';
import { Tag } from '~/common/components/TagListInput/styles';
import { Modal } from '~/common/components/Modal';
import { FormLabel } from '~/common/components/FormLabel';
import { IDropdownOption } from '~/common/components/Dropdown/types';
import { Dropdown } from '~/common/components/Dropdown';
import { Button } from '~/common/components/Button';
import { api } from '~/common/api';

import {
  Base64Wrapper,
  Footer,
  FormContainer,
  MediaInputRow,
  MediaPreviewGrid,
  MediaPreviewRemoveButton,
  MediaPreviewWrapper,
  OptionsTagList,
} from './styles';
import { IPromptRequest } from '../../interfaces/i-prompt-request';
import { IPromptDetails } from '../../interfaces/i-prompt-details';
import {
  PromptTypesEnum,
  PromptTypesFormattedEnum,
} from '../../enums/prompt-types-enum';
import {
  isVideoMediaUrl,
  toPromptMediaArray,
} from '../../utils/normalize-prompt-media';

type TRequestErrors = {
  planTier: string;
  type: string;
  title: string;
  prompt: string;
  mediaInput: string;
  jsonInput: string;
  githubUrl: string;
};

const DEFAULT_NON_ADMIN_PLAN_TIER = UserSubscriptionTypesEnum.PREMIUM;

interface IPromptFormModalProps {
  open: boolean;
  updating?: IPromptDetails;
  save: (request: IPromptRequest) => Promise<void>;
  close: () => void;
}
export const PromptFormModal: FC<IPromptFormModalProps> = ({
  open,
  updating,
  save,
  close,
}) => {
  const { showToast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.type === UserTypesEnum.ADMIN;
  const isMobile = useMediaQuery('(max-width: 750px)');
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loadingCategoryOptions, setLoadingCategoryOptions] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState<IDropdownOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadingFileName, setUploadingFileName] = useState<string>('');
  const [mediaUrlDraft, setMediaUrlDraft] = useState('');
  const [formData, setFormData] = useState<IPromptRequest>(
    {} as IPromptRequest
  );

  const [errors, setErrors] = useState<TRequestErrors>({
    planTier: '',
    type: '',
    title: '',
    prompt: '',
    mediaInput: '',
    jsonInput: '',
    githubUrl: '',
  });

  const clearBeforeClose = useCallback((): void => {
    setFormData({} as IPromptRequest);
    setLoading(false);
    setUploadingFileName('');
    setMediaUrlDraft('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setErrors({
      planTier: '',
      type: '',
      title: '',
      prompt: '',
      mediaInput: '',
      jsonInput: '',
      githubUrl: '',
    });

    close();
  }, [close]);

  const loadCategoryOptions = useCallback(async () => {
    try {
      setLoadingCategoryOptions(true);
      const { data: response } = await api.get<{
        categories: ICategoryResponse[];
      }>('/categories', {
        params: {
          type: formData.type,
        },
      });

      const toSet: IDropdownOption[] = [];
      for (const category of response.categories) {
        const { options, title } = category;
        for (const option of options) {
          if (!toSet.find((o) => o.value === option.value)) {
            toSet.push({
              value: option.value,
              label: `${title} - ${option.label}`,
            });
          }
        }
      }

      setCategoryOptions(toSet);
    } catch {
      showToast({
        severity: 'error',
        description: 'Erro ao carregar as opções de categorias',
      });
    } finally {
      setLoadingCategoryOptions(false);
    }
  }, [formData, showToast]);

  const getMediaItems = useCallback(
    (mediaInput?: IPromptRequest['mediaInput']): string[] =>
      toPromptMediaArray(mediaInput),
    []
  );

  const buildMediaRequestValue = useCallback(
    (mediaItems: string[]): IPromptRequest['mediaInput'] => {
      if (mediaItems.length <= 1) {
        return mediaItems[0] || '';
      }

      return mediaItems;
    },
    []
  );

  const handleClearFileInput = useCallback((): void => {
    setUploadingFileName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const setMediaItems = useCallback(
    (mediaItems: string[]): void => {
      setFormData((prev) => ({
        ...prev,
        mediaInput: buildMediaRequestValue(mediaItems),
      }));
      setErrors((prev) => ({
        ...prev,
        mediaInput: '',
      }));
    },
    [buildMediaRequestValue]
  );

  const readFileAsDataUrl = useCallback(
    (file: File): Promise<string> =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onloadend = () => {
          if (typeof reader.result === 'string') {
            resolve(reader.result);
            return;
          }

          reject(new Error('Falha ao converter o arquivo para base64.'));
        };

        reader.onerror = () => {
          reject(new Error('Falha ao ler o arquivo selecionado.'));
        };

        reader.readAsDataURL(file);
      }),
    []
  );

  const handleInputFileChange: ChangeEventHandler<HTMLInputElement> =
    useCallback(
      async (event) => {
        const files = Array.from(event.target.files || []);

        if (!files.length) {
          return;
        }

        try {
          const base64Files = await Promise.all(
            files.map((file) => readFileAsDataUrl(file))
          );

          setUploadingFileName(
            files.length === 1
              ? files[0].name || '1 arquivo adicionado'
              : `${files.length} arquivos adicionados`
          );

          setMediaItems([
            ...getMediaItems(formData.mediaInput),
            ...base64Files,
          ]);
        } catch {
          showToast({
            severity: 'error',
            description: 'Não foi possível processar os arquivos selecionados.',
          });
        } finally {
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }
      },
      [
        formData.mediaInput,
        getMediaItems,
        readFileAsDataUrl,
        setMediaItems,
        showToast,
      ]
    );

  const handleInputChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      const { name, value } = event.target;
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    },
    []
  );

  const handleAddMediaUrls = useCallback(() => {
    const newMediaItems = mediaUrlDraft
      .split('\n')
      .map((item) => item.trim())
      .filter(Boolean);

    if (!newMediaItems.length) {
      return;
    }

    setMediaItems([...getMediaItems(formData.mediaInput), ...newMediaItems]);
    setMediaUrlDraft('');
  }, [formData.mediaInput, getMediaItems, mediaUrlDraft, setMediaItems]);

  const handleRemoveMediaItem = useCallback(
    (indexToRemove: number) => {
      const remainingMediaItems = getMediaItems(formData.mediaInput).filter(
        (_, index) => index !== indexToRemove
      );

      setMediaItems(remainingMediaItems);

      if (!remainingMediaItems.length) {
        handleClearFileInput();
      }
    },
    [formData.mediaInput, getMediaItems, handleClearFileInput, setMediaItems]
  );

  const handleDropdwownChange = useCallback((value: string, name: string) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      prompt: '',
      jsonInput: '',
      ...(name === 'type' ? { categoryOptionIds: [] } : {}),
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: '',
    }));
  }, []);

  const handleSubmit: FormEventHandler = useCallback(
    (event) => {
      event.preventDefault();
      const formErrors: TRequestErrors = {} as TRequestErrors;
      const planTier = isAdmin
        ? formData.planTier
        : DEFAULT_NON_ADMIN_PLAN_TIER;

      if (!planTier) {
        formErrors.planTier = 'Plano é obrigatório';
      }
      if (!formData.type) {
        formErrors.type = 'Tipo é obrigatório';
      }
      if (!formData.title) {
        formErrors.title = 'Título é obrigatório';
      }
      if (!formData.prompt) {
        formErrors.prompt = 'Prompt é obrigatório';
      }
      if (getMediaItems(formData.mediaInput).length === 0) {
        formErrors.mediaInput = 'Mídia é obrigatória';
      }

      if (Object.keys(formErrors).length > 0) {
        setErrors(formErrors);
        return;
      }

      setErrors({} as TRequestErrors);
      setLoading(true);
      save({
        ...formData,
        planTier,
        mediaInput: buildMediaRequestValue(getMediaItems(formData.mediaInput)),
      })
        .then(() => {
          clearBeforeClose();
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [
      buildMediaRequestValue,
      clearBeforeClose,
      formData,
      getMediaItems,
      isAdmin,
      save,
    ]
  );

  const handleToggleCategoryOption = useCallback((option: string) => {
    setFormData((prev) => {
      const currentIds = prev.categoryOptionIds || [];

      if (currentIds.includes(option)) {
        return {
          ...prev,
          categoryOptionIds: currentIds.filter((id) => id !== option),
        };
      }
      return {
        ...prev,
        categoryOptionIds: [...currentIds, option],
      };
    });
  }, []);

  const availablePlanTierOptions = useMemo(() => {
    const allOptions = Object.entries(UserSubscriptionTypesEnumFormatted).map(
      ([value, label]) => ({
        value,
        label,
      })
    );

    if (user?.subscriptionAccessSource !== 'WHITELIST') {
      return allOptions;
    }

    if (user?.whitelistAccessTier === 'BASIC') {
      return allOptions.filter((option) => option.value !== 'PREMIUM');
    }

    return allOptions;
  }, [user?.subscriptionAccessSource, user?.whitelistAccessTier]);

  useEffect(() => {
    if (updating) {
      setFormData({
        title: updating.title,
        type: updating.type,
        prompt: updating.prompt,
        mediaInput: buildMediaRequestValue(toPromptMediaArray(updating.mediaUrl)),
        jsonInput: updating.json,
        githubUrl: updating.githubUrl,
        planTier: updating.planTier,
        categoryOptionIds: (updating.categoryOptions || []) as string[],
      });
      setUploadingFileName('');
      setMediaUrlDraft('');
    } else {
      setFormData({} as IPromptRequest);
      setUploadingFileName('');
      setMediaUrlDraft('');
    }
  }, [buildMediaRequestValue, updating]);

  useEffect(() => {
    if (!formData.planTier) {
      return;
    }

    const availablePlanTiers = availablePlanTierOptions.map(
      (option) => option.value
    );

    if (!availablePlanTiers.includes(formData.planTier)) {
      setFormData((prev) => ({
        ...prev,
        planTier: availablePlanTiers[availablePlanTiers.length - 1] as IPromptRequest['planTier'],
      }));
    }
  }, [availablePlanTierOptions, formData.planTier]);

  useEffect(() => {
    if (formData.type) {
      loadCategoryOptions();
    } else {
      setCategoryOptions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.type]);

  const availableCategoryOptions = useMemo(() => {
    if (!formData.type) {
      return [];
    }

    if (!categoryOptions || categoryOptions.length === 0) {
      return [];
    }

    if (
      !formData.categoryOptionIds ||
      formData.categoryOptionIds.length === 0
    ) {
      return categoryOptions;
    }

    return categoryOptions.filter(
      (option) => !formData.categoryOptionIds.includes(option.value as string)
    );
  }, [categoryOptions, formData]);

  const normalizeMediaSource = useCallback((source?: string): string => {
    if (!source) return '';
    return source.trim();
  }, []);

  const previewSources = useMemo(
    () =>
      getMediaItems(formData.mediaInput).map((source) =>
        normalizeMediaSource(source)
      ),
    [formData.mediaInput, getMediaItems, normalizeMediaSource]
  );

  const isVideoPrompt = useMemo(() => {
    return formData.type === PromptTypesEnum.VIDEO;
  }, [formData.type]);

  return (
    <Modal
      isOpen={open}
      onClose={clearBeforeClose}
      id="prompt-modal"
      height={isMobile ? '100%' : '90vh'}
      width="750px"
      title={updating ? 'Editar Prompt' : 'Novo Prompt'}
      justifyContent="flex-start"
    >
      <FormContainer onSubmit={handleSubmit}>
        {isAdmin && (
          <Dropdown
            name="planTier"
            width="100%"
            error={errors.planTier}
            backgroundColor="neutral900"
            onHoverBackgroundColor="neutral800"
            onFocusBorderColor="neutral400"
            borderColor="neutral800"
            currentOptionTextColor="neutral0"
            dropdownOptionTextColor="neutral0"
            dropdownBorderColor="neutral800"
            dropdownSelectedOptionBackgroundColor="neutral800"
            label={{
              label: 'Plano do Prompt',
              isRequired: true,
            }}
            value={formData.planTier}
            onChange={handleDropdwownChange}
            options={availablePlanTierOptions}
          />
        )}

        <Dropdown
          name="type"
          width="100%"
          error={errors.type}
          backgroundColor="neutral900"
          onHoverBackgroundColor="neutral800"
          onFocusBorderColor="neutral400"
          borderColor="neutral800"
          currentOptionTextColor="neutral0"
          dropdownOptionTextColor="neutral0"
          dropdownBorderColor="neutral800"
          dropdownSelectedOptionBackgroundColor="neutral800"
          label={{
            label: 'Tipo do Prompt',
            isRequired: true,
          }}
          value={formData.type}
          onChange={handleDropdwownChange}
          options={Object.entries(PromptTypesFormattedEnum).map(
            ([value, label]) => ({
              value,
              label,
            })
          )}
        />

        <Dropdown
          name="categoryOptionIds"
          width="100%"
          onChange={handleToggleCategoryOption}
          label={{ label: 'Opções de Categorias' }}
          options={availableCategoryOptions}
          isDisabled={!formData.type || loadingCategoryOptions}
          backgroundColor="neutral900"
          onHoverBackgroundColor="neutral800"
          onFocusBorderColor="neutral400"
          borderColor="neutral800"
          currentOptionTextColor="neutral0"
          dropdownOptionTextColor="neutral0"
          dropdownBorderColor="neutral800"
          dropdownSelectedOptionBackgroundColor="neutral800"
          value={null}
          placeholder={
            !formData.type
              ? 'Selecione um tipo para carregar as opções'
              : loadingCategoryOptions
              ? 'Carregando opções...'
              : 'Selecione as opções'
          }
        />

        <OptionsTagList>
          {formData.categoryOptionIds?.map((optionId) => {
            const option = categoryOptions.find(
              (opt) => opt.value === optionId
            );

            if (!option) return null;

            return (
              <li
                key={optionId}
                onClick={() => handleToggleCategoryOption(optionId)}
              >
                <Tag>
                  <Typography.Label size="x-small" color="neutral1000">
                    {option.label}
                  </Typography.Label>

                  <span id="x-close">&times;</span>
                </Tag>
              </li>
            );
          })}
        </OptionsTagList>

        <TextInput
          name="title"
          width="100%"
          error={errors.title}
          value={formData.title}
          onChange={handleInputChange}
          backgroundColor="neutral900"
          onHoverBackgroundColor="neutral800"
          onFocusBorderColor="neutral400"
          borderColor="neutral800"
          label={{
            label: 'Título',
            isRequired: true,
          }}
        />

        <TextInput
          name="prompt"
          as="textarea"
          width="100%"
          error={errors.prompt}
          // style={{ minHeight: '90px' }}
          value={formData.prompt}
          onChange={handleInputChange}
          backgroundColor="neutral900"
          onHoverBackgroundColor="neutral800"
          onFocusBorderColor="neutral400"
          borderColor="neutral800"
          label={{
            label: 'Prompt',
            isRequired: true,
          }}
        />

        {formData.type === PromptTypesEnum.SYSTEM_PROMPT && (
          <TextInput
            name="githubUrl"
            width="100%"
            value={formData.githubUrl}
            onChange={handleInputChange}
            backgroundColor="neutral900"
            onHoverBackgroundColor="neutral800"
            onFocusBorderColor="neutral400"
            borderColor="neutral800"
            label={{
              label: 'URL GitHub',
            }}
          />
        )}

        <Base64Wrapper>
          <input
            hidden
            accept={isVideoPrompt ? 'video/*' : 'image/*'}
            type='file'
            multiple
            ref={fileInputRef}
            onChange={handleInputFileChange}
          />

          <FormLabel
            label={isVideoPrompt ? 'Upload de videos' : 'Upload de imagens'}
            sublabel='Voce pode selecionar um ou mais arquivos e tambem adicionar URLs em lote, uma por linha.'
          />
          <Button
            width="100%"
            type="button"
            text={uploadingFileName || 'Selecionar arquivos'}
            backgroundColor={theme.neutral900}
            borderColor={theme.transparent}
            textOrIconColor={theme.neutral0}
            icon={FaUpload}
            justifyContent="center"
            onClick={() => fileInputRef.current?.click()}
          />
        </Base64Wrapper>

        <MediaInputRow>
          <TextInput
            name="mediaUrlDraft"
            as="textarea"
            width="100%"
            error={errors.mediaInput}
            value={mediaUrlDraft}
            onChange={(event) => {
              setMediaUrlDraft(event.target.value);
              setErrors((prev) => ({
                ...prev,
                mediaInput: '',
              }));
            }}
            backgroundColor="neutral900"
            onHoverBackgroundColor="neutral800"
            onFocusBorderColor="neutral400"
            borderColor="neutral800"
            label={{
              label: isVideoPrompt ? 'URLs dos videos' : 'URLs das imagens',
              isRequired: true,
            }}
          />

          <Button
            type="button"
            text="Adicionar URLs"
            backgroundColor={theme.neutral800}
            textOrIconColor={theme.neutral0}
            borderColor={theme.transparent}
            justifyContent="center"
            onClick={handleAddMediaUrls}
          />
        </MediaInputRow>

        {!!previewSources.length && (
          <MediaPreviewGrid>
            {previewSources.map((previewSource, index) => (
              <MediaPreviewWrapper key={`${previewSource}-${index}`}>
                {isVideoMediaUrl(previewSource) ? (
                  <video src={previewSource} muted playsInline loop autoPlay />
                ) : (
                  <img src={previewSource} alt={`Pre-visualizacao ${index + 1}`} />
                )}

                <MediaPreviewRemoveButton
                  type="button"
                  onClick={() => handleRemoveMediaItem(index)}
                >
                  <FaTrash size={12} />
                </MediaPreviewRemoveButton>
              </MediaPreviewWrapper>
            ))}
          </MediaPreviewGrid>
        )}

        <Footer>
          <Button
            text="Cancelar"
            onClick={clearBeforeClose}
            backgroundColor={theme.neutral900}
            textOrIconColor={theme.neutral400}
            borderColor={theme.neutral400}
            width="100%"
            justifyContent="center"
          />

          <Button
            type="submit"
            text="Salvar"
            backgroundColor={theme.primary}
            textOrIconColor={theme.neutral1000}
            borderColor={theme.transparent}
            width="100%"
            justifyContent="center"
            loading={loading}
            loader="bar"
            loaderColor="neutral1000"
          />
        </Footer>
      </FormContainer>
    </Modal>
  );
};
